import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { useTheme } from '../../contexts/ThemeContext';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { BrandMark } from '../BrandMark';

interface SearchSuggestion {
  type: 'log';
  value: string;
  label: string;
  description?: string;
}

interface SearchBarProps {
  placeholder?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({ placeholder }) => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Build a direct transmission-message search suggestion without consulting
  // the retired client/service inventory.
  useEffect(() => {
    const buildSuggestion = () => {
      if (searchValue.trim().length < 2) {
        setSuggestions([]);
        return;
      }
      setSuggestions([{
        type: 'log',
        value: searchValue,
        label: `Search transmission logs for "${searchValue}"`,
        description: 'Press Enter to search messages',
      }]);
    };

    const debounceTimer = setTimeout(buildSuggestion, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchValue]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (query: string) => {
    if (query.trim()) {
      navigate({ to: '/logs', search: { search: query.trim() } });
      setShowSuggestions(false);
      setSearchValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        handleSuggestionClick(suggestions[selectedIndex]);
      } else {
        handleSearch(searchValue);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    handleSearch(suggestion.value);
    setShowSuggestions(false);
    setSearchValue('');
  };

  return (
    <div className="max-w-2xl w-full relative">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="h-5 w-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          ref={inputRef}
          type="text"
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            setShowSuggestions(true);
            setSelectedIndex(-1);
          }}
          onFocus={() => searchValue.trim().length >= 2 && setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="block w-full pl-10 pr-3 py-2 border border-input rounded-lg leading-5 bg-background placeholder:text-muted-foreground focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-ring sm:text-sm"
        />
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-full bg-card text-card-foreground rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-white dark:ring-opacity-10 max-h-80 overflow-auto"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={`${suggestion.type}-${suggestion.value}-${index}`}
              onClick={() => handleSuggestionClick(suggestion)}
              className={`w-full px-4 py-3 text-left hover:bg-accent flex items-start gap-3 ${
                index === selectedIndex ? 'bg-accent' : ''
              } ${index === 0 ? 'rounded-t-lg' : ''} ${
                index === suggestions.length - 1 ? 'rounded-b-lg' : ''
              }`}
            >
              <div className="flex-shrink-0 mt-0.5">
                <svg className="h-5 w-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{suggestion.label}</p>
                {suggestion.description && (
                  <p className="text-xs text-muted-foreground">{suggestion.description}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface NavbarProps {
  onToggleSidebar: () => void;
  isMobileMenuOpen: boolean;
}

export const Navbar: React.FC<NavbarProps> = ({ onToggleSidebar, isMobileMenuOpen }) => {
  const { t } = useTranslation(['dashboard', 'common']);
  const { user, logout } = useAuthStore();
  const { theme, toggleTheme } = useTheme();
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [showHelp, setShowHelp] = React.useState(false);
  const navigate = useNavigate();
  const helpRef = React.useRef<HTMLDivElement>(null);

  // Close help dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (helpRef.current && !helpRef.current.contains(event.target as Node)) {
        setShowHelp(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate({ to: '/login' });
  };

  return (
    <nav className="bg-card text-card-foreground border-b fixed w-full z-30 top-0">
      <div className="px-3 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Left side - Logo and toggle */}
          <div className="flex items-center">
            {/* Sidebar toggle button */}
            <button
              onClick={onToggleSidebar}
              className="mr-1 p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg focus:outline-none focus:ring-2 focus:ring-ring sm:mr-4"
              aria-label={isMobileMenuOpen ? 'Close navigation' : 'Open navigation'}
              aria-controls="app-sidebar"
              aria-expanded={isMobileMenuOpen}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <div className="flex-shrink-0 sm:hidden"><BrandMark compact /></div>
            <div className="hidden flex-shrink-0 sm:block"><BrandMark /></div>
          </div>

          {/* Center - Search bar */}
          <div className="hidden lg:flex flex-1 items-center justify-center px-8">
            <SearchBar placeholder={t('dashboard:search.placeholder')} />
          </div>

          {/* Right side - Actions and user menu */}
          <div className="flex shrink-0 items-center space-x-1 sm:space-x-2">
            {/* Language Switcher */}
            <LanguageSwitcher />
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring rounded-lg transition-colors"
              title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              {theme === 'light' ? (
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              )}
            </button>

            {/* Help */}
            <div className="relative" ref={helpRef}>
              <button
                onClick={() => setShowHelp(!showHelp)}
                className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring rounded-lg"
                aria-label={showHelp ? 'Close help menu' : 'Open help menu'}
                aria-expanded={showHelp}
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>

              {showHelp && (
                <div className="absolute right-0 mt-2 w-72 bg-card text-card-foreground rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-white dark:ring-opacity-10">
                  <div className="p-4 border-b">
                    <h3 className="text-sm font-semibold">{t('common:help.title')}</h3>
                  </div>
                  <div className="py-2">
                    <a
                      href="https://docs.loglimit.io"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-4 py-3 text-sm text-foreground hover:bg-accent"
                    >
                      <svg className="h-5 w-5 mr-3 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                      {t('common:help.documentation')}
                    </a>
                    <a
                      href="https://docs.loglimit.io/api"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-4 py-3 text-sm text-foreground hover:bg-accent"
                    >
                      <svg className="h-5 w-5 mr-3 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                      </svg>
                      {t('common:help.apiReference')}
                    </a>
                    <a
                      href="https://status.loglimit.io"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-4 py-3 text-sm text-foreground hover:bg-accent"
                    >
                      <svg className="h-5 w-5 mr-3 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {t('common:help.systemStatus')}
                    </a>
                    <button
                      onClick={() => {
                        setShowHelp(false);
                        // Open keyboard shortcuts modal or show toast with shortcuts
                        alert(t('common:help.keyboardShortcutsContent'));
                      }}
                      className="flex items-center w-full px-4 py-3 text-sm text-foreground hover:bg-accent text-left"
                    >
                      <svg className="h-5 w-5 mr-3 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
                      </svg>
                      {t('common:help.keyboardShortcuts')}
                    </button>
                  </div>
                  <div className="border-t py-2">
                    <a
                      href="mailto:support@loglimit.io"
                      className="flex items-center px-4 py-3 text-sm text-foreground hover:bg-accent"
                    >
                      <svg className="h-5 w-5 mr-3 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {t('common:help.contactSupport')}
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* User dropdown */}
            <div className="relative ml-0.5 sm:ml-3">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center space-x-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring rounded-lg p-1 hover:bg-accent sm:space-x-3 sm:p-1.5"
                aria-label={showDropdown ? 'Close account menu' : 'Open account menu'}
                aria-expanded={showDropdown}
              >
                <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-sm font-medium text-primary-foreground">
                    {user?.email?.[0].toUpperCase()}
                  </span>
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium">{user?.email}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                </div>
                <svg className="h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showDropdown && (
                <div className="absolute right-0 mt-2 w-56 bg-card text-card-foreground rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-white dark:ring-opacity-10">
                  <div className="p-3 border-b">
                    <p className="text-sm font-medium">{user?.email}</p>
                    <p className="text-xs text-muted-foreground capitalize">Role: {user?.role}</p>
                  </div>
                  <div className="py-1">
                    <Link
                      to="/settings"
                      className="block px-4 py-2 text-sm text-foreground hover:bg-accent"
                    >
                      Account Settings
                    </Link>
                    <Link
                      to="/billing"
                      className="block px-4 py-2 text-sm text-foreground hover:bg-accent"
                    >
                      Billing
                    </Link>
                  </div>
                  <div className="py-1 border-t">
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-destructive hover:bg-destructive/10"
                    >
                      {t('common:actions.logout')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};
